import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { QueueItem, QueueSessionEnvelope, QueueSessionSummary } from './queueApi';
import { postQueueProgress, type QueueProgressUpdatePayload } from './queueApi';
import { isVerboseLoggingEnabled } from '../debug/logging';

const DEFAULT_HEARTBEAT_INTERVAL_MS = 12_000;
const MIN_PROGRESS_INTERVAL_MS = 2_000;
const PROGRESS_EPSILON_MS = 500;

const LOG_NAMESPACE = '[QueueProgress]';
const isDevelopmentEnv = typeof process !== 'undefined' ? process.env.NODE_ENV !== 'production' : true;
const isDevelopment = isDevelopmentEnv && isVerboseLoggingEnabled();

export type ProgressSummary = {
  itemId: string | null;
  itemIndex: number;
  progressMs: number;
};

export type ProgressTriggerOptions = {
  force?: boolean;
  keepalive?: boolean;
  reason?:
    | 'pause'
    | 'seek'
    | 'heartbeat'
    | 'heartbeat-hidden'
    | 'visibility'
    | 'before-unload'
    | 'manual'
    | 'pagehide'
    | 'resolution'
    | 'unspecified';
  resolvedItemId?: string | null;
  resolvedTrackUri?: string | null;
};

type UseQueueProgressReporterOptions = {
  session: QueueSessionSummary | null;
  items: QueueItem[];
  playerState: Spotify.PlaybackState | null;
  deviceId: string | null;
  isPlaying: boolean;
  enabled: boolean;
  heartbeatIntervalMs?: number;
  onProgressAcknowledged?: (envelope: QueueSessionEnvelope) => void;
};

type Snapshot = {
  payload: QueueProgressUpdatePayload;
  summary: ProgressSummary;
};

const resolvePointerIndex = (session: QueueSessionSummary | null): number => {
  if (!session) return 0;
  const pointer = Number.isFinite(session.currentItemIndex) ? session.currentItemIndex : 0;
  return pointer >= 0 ? pointer : 0;
};

const derivePointerItemId = (
  session: QueueSessionSummary,
  items: QueueItem[],
  index: number
): string | null => {
  if (session.currentItemId) {
    return session.currentItemId;
  }
  const candidate = items[index] ?? null;
  return candidate ? candidate.id : null;
};

export const useQueueProgressReporter = ({
  session,
  items,
  playerState,
  deviceId,
  isPlaying,
  enabled,
  heartbeatIntervalMs,
  onProgressAcknowledged
}: UseQueueProgressReporterOptions) => {
  const lastSentAtRef = useRef<number>(0);
  const lastSummaryRef = useRef<ProgressSummary | null>(null);
  const sendingRef = useRef(false);
  const wasPlayingRef = useRef<boolean>(false);
  const latestSummaryRef = useRef<ProgressSummary | null>(null);
  const lastNonZeroSummaryRef = useRef<ProgressSummary | null>(null);
  const lastNonZeroProgressUpdatedAtRef = useRef<number>(0);

  const activeSessionId = session?.id ?? null;

  const logProgressEvent = useCallback((event: string, details?: Record<string, unknown>) => {
    if (!isDevelopment) return;
    if (details) {
      console.debug(LOG_NAMESPACE, event, details);
    } else {
      console.debug(LOG_NAMESPACE, event);
    }
  }, []);

  useEffect(() => {
    lastSentAtRef.current = 0;
    lastSummaryRef.current = null;
    latestSummaryRef.current = null;
    lastNonZeroSummaryRef.current = null;
    lastNonZeroProgressUpdatedAtRef.current = 0;
  }, [activeSessionId]);

  const computeSnapshot = useCallback((): Snapshot | null => {
    if (!session) return null;
    if (session.status === 'stale') return null;

    const pointerIndex = resolvePointerIndex(session);
    const pointerItemId = derivePointerItemId(session, items, pointerIndex);

    const playerPosition =
      typeof playerState?.position === 'number' && Number.isFinite(playerState.position)
        ? Math.max(0, Math.floor(playerState.position))
        : null;

    const progressMs =
      playerPosition != null
        ? playerPosition
        : Math.max(0, Math.floor(session.currentProgressMs ?? 0));

    const payload: QueueProgressUpdatePayload = {
      currentItemIndex: pointerIndex,
      currentProgressMs: progressMs,
      expectedRevision: session.revision,
      reportedAt: new Date().toISOString()
    };

    if (pointerItemId) {
      payload.currentItemId = pointerItemId;
    }

    if (deviceId) {
      payload.deviceId = deviceId;
    }

    const summary: ProgressSummary = {
      itemId: pointerItemId,
      itemIndex: pointerIndex,
      progressMs
    };

    return { payload, summary };
  }, [deviceId, items, playerState, session]);

  useEffect(() => {
    if (!enabled) return;
    if (!session || session.status === 'stale') return;

    const snapshot = computeSnapshot();
    if (!snapshot) return;

    latestSummaryRef.current = snapshot.summary;
    if (snapshot.summary.progressMs > 0) {
      lastNonZeroSummaryRef.current = snapshot.summary;
      lastNonZeroProgressUpdatedAtRef.current = Date.now();
    }
  }, [computeSnapshot, enabled, session]);

  const sendProgress = useCallback(
    async (options?: ProgressTriggerOptions): Promise<boolean> => {
      const reason = options?.reason ?? 'unspecified';
      if (!enabled) return false;
      if (!session || session.status === 'stale') return false;
      if (sendingRef.current) {
        logProgressEvent('skip-active-request', { reason });
        return false;
      }

      const snapshot = computeSnapshot();
      if (!snapshot) return false;

      const { payload, summary } = snapshot;
      const now = Date.now();
      const force = options?.force ?? false;

      latestSummaryRef.current = summary;
      if (summary.progressMs > 0) {
        lastNonZeroSummaryRef.current = summary;
        lastNonZeroProgressUpdatedAtRef.current = now;
      }

      if (!force && lastSentAtRef.current) {
        const elapsed = now - lastSentAtRef.current;
        if (elapsed < MIN_PROGRESS_INTERVAL_MS) {
          logProgressEvent('skip-too-frequent', { reason, elapsed });
          return false;
        }
      }

      if (lastSummaryRef.current) {
        const previous = lastSummaryRef.current;
        const sameItem = previous.itemId === summary.itemId && previous.itemIndex === summary.itemIndex;
        const progressDelta = Math.abs(previous.progressMs - summary.progressMs);
        if (!force && sameItem && progressDelta < PROGRESS_EPSILON_MS) {
          logProgressEvent('skip-small-delta', { reason, progressDelta });
          return false;
        }
        if (force && sameItem && progressDelta < PROGRESS_EPSILON_MS / 2) {
          logProgressEvent('skip-small-delta-force', { reason, progressDelta });
          return false;
        }
      }

      if (options?.resolvedItemId && options?.resolvedTrackUri) {
        payload.resolvedItemId = options.resolvedItemId;
        payload.resolvedTrackUri = options.resolvedTrackUri;
      }

      lastSentAtRef.current = now;
      lastSummaryRef.current = summary;
      sendingRef.current = true;

      try {
        logProgressEvent('send-start', {
          reason,
          keepalive: options?.keepalive ?? false,
          itemId: summary.itemId,
          itemIndex: summary.itemIndex,
          progressMs: summary.progressMs,
          expectedRevision: payload.expectedRevision
        });
        const envelope = await postQueueProgress(payload, {
          keepalive: options?.keepalive ?? false
        });
        onProgressAcknowledged?.(envelope);
        logProgressEvent('send-success', {
          reason,
          revision: envelope.session.revision,
          status: envelope.session.status
        });
        return true;
      } catch (error) {
        logProgressEvent('send-error', {
          reason,
          keepalive: options?.keepalive ?? false,
          error: error instanceof Error ? error.message : String(error)
        });
        console.warn('[Queue] Progress update failed', {
          error,
          payload,
          reason: options?.reason ?? 'unspecified'
        });
        return false;
      } finally {
        sendingRef.current = false;
      }
    },
    [computeSnapshot, enabled, logProgressEvent, onProgressAcknowledged, session]
  );

  const helpers = useMemo(
    () => ({
      sendProgress,
      getLastProgressSummary: () => latestSummaryRef.current,
      getLastNonZeroProgressSummary: () => lastNonZeroSummaryRef.current,
      getLastNonZeroProgressUpdatedAt: () => lastNonZeroProgressUpdatedAtRef.current
    }),
    [sendProgress]
  );

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const handleBeforeUnload = () => {
      void sendProgress({ force: true, keepalive: true, reason: 'before-unload' });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, sendProgress]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof document === 'undefined') return;

    const handleVisibility = () => {
      logProgressEvent('visibilitychange', { state: document.visibilityState });
      if (document.visibilityState === 'hidden') {
        void sendProgress({ force: true, keepalive: true, reason: 'visibility' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, logProgressEvent, sendProgress]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    const handlePageHide = (event: PageTransitionEvent) => {
      logProgressEvent('pagehide', { persisted: event.persisted });
      void sendProgress({ force: true, keepalive: true, reason: 'pagehide' });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [enabled, logProgressEvent, sendProgress]);

  useEffect(() => {
    if (!enabled) return;
    if (!isPlaying) return;
    if (!session) return;
    if (typeof window === 'undefined') return;

    const intervalMs = Math.max(5_000, heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS);
    const timer = window.setInterval(() => {
      const hidden =
        typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false;
      const reason = hidden ? 'heartbeat-hidden' : 'heartbeat';
      if (hidden) {
        logProgressEvent('heartbeat-hidden', { intervalMs });
      } else {
        logProgressEvent('heartbeat', { intervalMs });
      }
      void sendProgress({ reason, ...(hidden ? { keepalive: true } : {}) });
    }, intervalMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [enabled, heartbeatIntervalMs, isPlaying, logProgressEvent, sendProgress, session]);

  useEffect(() => {
    const wasPlaying = wasPlayingRef.current;
    wasPlayingRef.current = isPlaying;

    if (!enabled) {
      return;
    }
    if (!session || session.status === 'stale') {
      return;
    }
    if (wasPlaying && !isPlaying) {
      void sendProgress({ force: true, reason: 'pause' });
    }
  }, [enabled, isPlaying, sendProgress, session]);

  return helpers;
};
